import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Using Record<string,any> base because API returns Drizzle camelCase
// fields that differ from the shared MonitoraggioWithPrice type
type MonitorData = Record<string, any>;
import { findAirportByCode } from '@flymind/shared';
import { useTranslation } from '../hooks/useTranslation';
import { Card, AlertBadge, Badge, Button } from './ui';

interface MonitorCardProps {
  monitor: MonitorData;
  onToggleActive: (id: string, attivo: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

function formatDateFull(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatPrice(price: number | null | undefined): string {
  if (price == null) return '--';
  return `${price.toFixed(0)}\u20AC`;
}

function TrendArrow({ variazione }: { variazione: number | null | undefined }) {
  const { t } = useTranslation();
  if (variazione == null || variazione === 0) {
    return <span className="text-gray-400 text-lg" aria-label={t('monitorCard.trendStable')}>&rarr;</span>;
  }
  if (variazione > 0) {
    return <span className="text-red-500 text-lg" aria-label={t('monitorCard.trendUp')}>&uarr;</span>;
  }
  return <span className="text-green-500 text-lg" aria-label={t('monitorCard.trendDown')}>&darr;</span>;
}

export function MonitorCard({ monitor, onToggleActive, onDelete }: MonitorCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsToggling(true);
    try {
      await onToggleActive(monitor.id, !monitor.attivo);
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(true);
    try {
      await onDelete(monitor.id);
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const depAirport = findAirportByCode(monitor.aeroportoPartenza);
  const arrAirport = findAirportByCode(monitor.aeroportoArrivo);
  const depCity = depAirport?.city ?? monitor.aeroportoPartenza;
  const arrCity = arrAirport?.city ?? monitor.aeroportoArrivo;
  const perPax = monitor.ultimoPrezzo != null ? Number(monitor.ultimoPrezzo) : null;
  const pax = Math.max(1, Number(monitor.passeggeri ?? 1));
  const prezzo = perPax != null ? perPax * pax : null;

  const budgetPercent =
    monitor.budgetMax && prezzo
      ? Math.min(100, Math.round((prezzo / Number(monitor.budgetMax)) * 100))
      : null;

  const isFlessibile = monitor.tipoMonitoraggio === 'flessibile';

  let dateRange: string;
  if (isFlessibile && monitor.periodoInizio && monitor.periodoFine) {
    dateRange = `${t('monitors.period')}: ${formatDate(monitor.periodoInizio)} - ${formatDateFull(monitor.periodoFine)}`;
  } else if (monitor.soloAndata) {
    dateRange = formatDateFull(monitor.dataAndata);
  } else {
    dateRange = `${formatDate(monitor.dataAndata)} - ${formatDateFull(monitor.dataRitorno!)}`;
  }

  return (
    <Card
      variant={monitor.attivo ? 'default' : 'default'}
      padding="none"
      className={`cursor-pointer transition-shadow hover:shadow-md ${!monitor.attivo ? 'opacity-60' : ''}`}
      onClick={() => navigate(`/monitor/${monitor.id}`)}
    >
      <div className="p-4 sm:p-5">
        {/* Route and alert badge */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <p className="text-lg font-semibold text-text-primary">
                {monitor.aeroportoPartenza} &rarr; {monitor.aeroportoArrivo}
              </p>
              <p className="text-sm text-text-secondary">
                {depCity} &rarr; {arrCity}
              </p>
            </div>
            {isFlessibile && (
              <Badge variant="info" size="sm">{t('monitors.flexible')}</Badge>
            )}
          </div>
          {monitor.statoAlert !== 'none' && <AlertBadge level={monitor.statoAlert} />}
        </div>

        {/* Dates */}
        <p className="text-sm text-text-secondary mb-3">{dateRange}</p>

        {/* Filter badges */}
        {(() => {
          const badges: string[] = [];
          if (monitor.classeCabina === 'premium_economy') badges.push(t('monitors.premiumEco'));
          else if (monitor.classeCabina === 'business') badges.push('Business');
          else if (monitor.classeCabina === 'first') badges.push('First');
          if (monitor.passeggeri > 1) badges.push(`${monitor.passeggeri} pax`);
          return badges.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {badges.map((label) => (
                <Badge key={label} variant="neutral" size="sm">{label}</Badge>
              ))}
            </div>
          ) : null;
        })()}

        {/* Price and trend */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-text-primary leading-tight">
              {formatPrice(prezzo)}
            </span>
            {pax > 1 && perPax != null && (
              <span className="text-xs text-text-secondary mt-0.5">
                {pax} pax · {formatPrice(perPax)}/pax
              </span>
            )}
          </div>
          <TrendArrow variazione={monitor.variazione} />
          {monitor.variazionePercentuale != null && (
            <span
              className={`text-sm font-medium ${
                monitor.variazionePercentuale > 0
                  ? 'text-red-500'
                  : monitor.variazionePercentuale < 0
                    ? 'text-green-500'
                    : 'text-gray-400'
              }`}
            >
              {monitor.variazionePercentuale > 0 ? '+' : ''}
              {monitor.variazionePercentuale.toFixed(1)}%
            </span>
          )}
        </div>

        {/* Budget bar */}
        {monitor.budgetMax && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-text-secondary mb-1">
              <span>{t('monitors.budget')}: {monitor.budgetMax}&euro;</span>
              {budgetPercent !== null && <span>{budgetPercent}%</span>}
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  budgetPercent !== null && budgetPercent >= 90
                    ? 'bg-red-500'
                    : budgetPercent !== null && budgetPercent >= 70
                      ? 'bg-orange-400'
                      : 'bg-green-500'
                }`}
                style={{ width: `${budgetPercent ?? 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Booking CTA */}
        {(monitor.statoAlert === 'good' || monitor.statoAlert === 'rare' || monitor.statoAlert === 'exceptional') && (
          <div className="mb-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/monitor/${monitor.id}`);
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors cursor-pointer"
            >
              {t('monitors.book')}
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <button
            type="button"
            onClick={handleToggle}
            disabled={isToggling}
            className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            aria-label={monitor.attivo ? t('monitor.deactivate') : t('monitor.activate')}
          >
            <div
              className={`relative w-9 h-5 rounded-full transition-colors ${
                monitor.attivo ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  monitor.attivo ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </div>
            <span>{monitor.attivo ? t('common.active') : t('common.inactive')}</span>
          </button>

          {!showConfirm ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowConfirm(true);
              }}
              className="text-sm text-text-secondary hover:text-red-500 transition-colors cursor-pointer"
              aria-label={t('monitor.delete')}
            >
              {t('common.delete')}
            </button>
          ) : (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="danger"
                size="sm"
                isLoading={isDeleting}
                onClick={handleDelete}
              >
                {t('common.confirm')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirm(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
